﻿import type * as CF from '@sa/services/interfaces/customFunction';
import { enqueueCall } from './batch.js';
import { excelToJSDate, flattenExcelRange, validatePositiveNumber } from '$lib/office/excelHelpers.js';

/**
 * Connects to Xero and returns the GL account balance filtered between two dates, and by tracking information.
 * @customfunction TRACK XTRACK
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Account Xero Account to process.
 * @param Category The Tracking category to filter.  Leaving this blank will use the 'Unassigned' category
 * @param Option The Tracking category's option to filter.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @returns Sum of accounts
 */
export function XTRACK(
    ScottsOrgId: string,
    Account: string,
    Category: string,
    Option: string,
    StartDate: number,
    EndDate: number
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (Account == null || Account == '0' || Account == '') {
        return OfficeExtension.Promise.reject('Account is missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.XTRACK = {
        funcName: 'XTRACK',
        scottsOrgId: ScottsOrgId,
        startAcct: Account,
        startDate: startDateString,
        endDate: endDateString,
        trackingCategory: Category,
        trackingOption: Option
    };
    return enqueueCall(body);
}

/**
 * Connects to Xero and sums the GL account balances between two account codes, two dates, and by tracking information.
 * @customfunction TRACKR XTRACKR
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartAccountCode First Account code to process.
 * @param EndAccountCode Last Account code to process.
 * @param Category The Tracking category to filter.  Leaving this blank will use the 'Unassigned' category
 * @param Option The Tracking category's option to filter.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param [IncludeArchived] Optional. Include all accounts in the Range, not just the active ones.
 * @returns Sum of accounts
 */
export function XTRACKR(
    ScottsOrgId: string,
    StartAccountCode: number,
    EndAccountCode: number,
    Category: string,
    Option: string,
    StartDate: number,
    EndDate: number,
    IncludeArchived?: boolean
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (!validatePositiveNumber(StartAccountCode)) {
        return OfficeExtension.Promise.reject('Start code is not valid.');
    }
    if (!validatePositiveNumber(EndAccountCode)) {
        return OfficeExtension.Promise.reject('End code is not valid.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.XTRACKR = {
        funcName: 'XTRACKR',
        scottsOrgId: ScottsOrgId,
        startAcct: StartAccountCode,
        endCode: EndAccountCode,
        startDate: startDateString,
        endDate: endDateString,
        trackingCategory: Category,
        trackingOption: Option,
        includeAll: IncludeArchived
    };
    return enqueueCall(body);
}

/**
 * Connects to Xero and sums the GL account balances filtered between two dates, and by two tracking informations.
 * @customfunction TRACKM XTRACKM
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Account Xero Account to process.
 * @param FirstCategory The first Tracking category to filter.  This must be filled in.
 * @param FirstOption The first Tracking category's option to filter.
 * @param SecondCategory The second Tracking category to filter.  Leaving this blank will sum the entries with only the first category.
 * @param SecondOption The second Tracking category's option to filter.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @returns Sum of accounts
 */
export function XTRACKM(
    ScottsOrgId: string,
    Account: string,
    FirstCategory: string,
    FirstOption: string,
    SecondCategory: string,
    SecondOption: string,
    StartDate: number,
    EndDate: number
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (Account == null || Account == '0' || Account == '') {
        return OfficeExtension.Promise.reject('Account is missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.XTRACKM = {
        funcName: 'XTRACKM',
        scottsOrgId: ScottsOrgId,
        startAcct: Account,
        startDate: startDateString,
        endDate: endDateString,
        trackingCategory: FirstCategory,
        trackingOption: FirstOption,
        trackingCategory2: SecondCategory,
        trackingOption2: SecondOption
    };
    return enqueueCall(body);
}

/**
 * Connects to Xero and returns the GL account balance for several accounts filtered between two dates, and by tracking information.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Category The Tracking category to filter.  Leaving this blank will use the 'Unassigned' category
 * @param Option The Tracking category's option to filter.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param Accounts GL account codes to process.
 * @returns Sum of accounts
 */
export function GLMULTITRACK(
    ScottsOrgId: string,
    Category: string,
    Option: string,
    StartDate: number,
    EndDate: number,
    Accounts: string[][][]
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    const accounts = flattenExcelRange(Accounts);
    if (accounts.length === 0) {
        return OfficeExtension.Promise.reject('Accounts are missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.GLMULTITRACK = {
        funcName: 'GLMULTITRACK',
        scottsOrgId: ScottsOrgId,
        startDate: startDateString,
        endDate: endDateString,
        trackingCategory: Category,
        trackingOption: Option,
        accounts: accounts,
    };
    return enqueueCall(body);
}

/**
 * Connects to Xero and returns the Net Income as reported by the P&L Report between two dates.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartDate Starting date of the range.
 * @param EndDate Final date of the range.
 * @param [FirstCategory] Optional. The first Tracking category to filter.
 * @param [FirstOption] Optional. The first Tracking category's option to filter.
 * @param [SecondCategory] Optional. The second Tracking category to filter.
 * @param [SecondOption] Optional. The second Tracking category's option to filter.
 * @returns Xero's Net Income
 */
 export function XNI(
    ScottsOrgId: string,
    StartDate: number,
    EndDate: number,
    FirstCategory?: string,
    FirstOption?: string,
    SecondCategory?: string,
    SecondOption?: string
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.XNI = {
        funcName: 'XNI',
        scottsOrgId: ScottsOrgId,
        startDate: startDateString,
        endDate: endDateString,
        trackingCategory: FirstCategory,
        trackingOption: FirstOption,
        trackingCategory2: SecondCategory,
        trackingOption2: SecondOption
    };
    return enqueueCall(body);
}

/**
 * Connects to Xero and returns invoice lines filtered between two dates and other filtering information.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartDate Starting date of the range to filter.
 * @param EndDate Final date of the range to filter.
 * @param [StartDueDate] Optional. Starting Due Date to filter.
 * @param [EndDueDate] Optional. Ending Due Date to filter.
 * @param [Item] Optional. Item to filter.
 * @param [Account] Optional. Xero Account to filter.
 * @param [FirstCategory] Optional. The first Tracking category to filter.
 * @param [FirstOption] Optional. The first Tracking category's option to filter.
 * @param [SecondCategory] Optional. The second Tracking category to filter.
 * @param [SecondOption] Optional. The second Tracking category's option to filter.
 * @param Contacts Contacts to filter.
 * @returns Invoice lines matching the filters.
 */
export function XINVOICE(
    ScottsOrgId: string,
    StartDate: number,
    EndDate: number,
    StartDueDate?: number,
    EndDueDate?: number,
    Item?: string,
    Account?: string,
    FirstCategory?: string,
    FirstOption?: string,
    SecondCategory?: string,
    SecondOption?: string,
    Contacts?: string[][][]
): Promise<Array<Array<unknown>>> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const startDueDateString = (StartDueDate ?? 0) > 0 ? excelToJSDate(StartDueDate!).toISOString() : null;
    const endDueDateString = (EndDueDate ?? 0) > 0 ? excelToJSDate(EndDueDate!).toISOString() : null;
    const body: CF.XINVOICE = {
        funcName: 'XINVOICE',
        scottsOrgId: ScottsOrgId,
        startDate: startDateString,
        endDate: endDateString,
        startDueDate: startDueDateString || undefined,
        endDueDate: endDueDateString || undefined,
        item: Item || undefined,
        startAcct: Account || undefined,
        trackingCategory: FirstCategory || undefined,
        trackingOption: FirstOption || undefined,
        trackingCategory2: SecondCategory || undefined,
        trackingOption2: SecondOption || undefined,
        contacts: flattenExcelRange(Contacts),
    };
    return enqueueCall(body);
}

/**
 * Connects to Xero and returns the sum of bill lines filtered between two issue dates, due dates, expected payment dates, and contacts.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartDate Starting date of the range to filter.
 * @param EndDate Final date of the range to filter.
 * @param [Status] Optional. Status to filter.
 * @param [StartDueDate] Optional. Starting Due Date to filter.
 * @param [EndDueDate] Optional. Ending Due Date to filter.
 * @param [StartExpectedPaymentDate] Optional. Starting Expected Payment Date to filter.
 * @param [EndExpectedPaymentDate] Optional. Ending Expected Payment Date to filter.
 * @param [ContactGroup] Optional. Contact Group to filter.
 * @param Contacts Contacts to filter.
 * @returns Invoice headers matching the filters.
 */
export function XINVOICE_HEADER(
    ScottsOrgId: string,
    StartDate: number,
    EndDate: number,
    Status?: string,
    StartDueDate?: number,
    EndDueDate?: number,
    StartExpectedPaymentDate?: number,
    EndExpectedPaymentDate?: number,
    ContactGroup?: string,
    Contacts?: string[][][]
): Promise<Array<Array<unknown>>> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const startDueDateString = (StartDueDate ?? 0) > 0 ? excelToJSDate(StartDueDate!).toISOString() : null;
    const endDueDateString = (EndDueDate ?? 0) > 0 ? excelToJSDate(EndDueDate!).toISOString() : null;
    const startExpectedPaymentDateString = (StartExpectedPaymentDate ?? 0) > 0 ? excelToJSDate(StartExpectedPaymentDate!).toISOString() : null;
    const endExpectedPaymentDateString = (EndExpectedPaymentDate ?? 0) > 0 ? excelToJSDate(EndExpectedPaymentDate!).toISOString() : null;
    Status = Status?.toLocaleUpperCase();
    if (Status == 'AWAITING PAYMENT') Status = 'AUTHORISED';
    if (Status == 'AWAITING APPROVAL') Status = 'SUBMITTED';
    const body: CF.XINVOICE_HEADER = {
        funcName: 'XINVOICE_HEADER',
        scottsOrgId: ScottsOrgId,
        startDate: startDateString,
        endDate: endDateString,
        status: Status || undefined,
        startDueDate: startDueDateString || undefined,
        endDueDate: endDueDateString || undefined,
        startExpectedPaymentDate: startExpectedPaymentDateString || undefined,
        endExpectedPaymentDate: endExpectedPaymentDateString || undefined,
        contactGroup: ContactGroup || undefined,
        contacts: flattenExcelRange(Contacts),
    };
    return enqueueCall(body);
}

/**
 * Connects to Xero and returns the Amount Due sum of invoice lines filtered between two issue dates, due dates, and contacts.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartDate Starting date of the range to filter.
 * @param EndDate Final date of the range to filter.
 * @param [Status] Optional. Status to filter.
 * @param [StartDueDate] Optional. Starting Due Date to filter.
 * @param [EndDueDate] Optional. Ending Due Date to filter.
 * @param [ContactGroup] Optional. Contact Group to filter.
 * @param Contacts Contacts to filter.
 * @returns Amount Due Sum of the invoices matching the filters.
 */
export function XINVOICE_SUM(
    ScottsOrgId: string,
    StartDate: number,
    EndDate: number,
    Status?: string,
    StartDueDate?: number,
    EndDueDate?: number,
    ContactGroup?: string,
    Contacts?: string[][][]
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const startDueDateString = (StartDueDate ?? 0) > 0 ? excelToJSDate(StartDueDate!).toISOString() : null;
    const endDueDateString = (EndDueDate ?? 0) > 0 ? excelToJSDate(EndDueDate!).toISOString() : null;
    Status = Status?.toLocaleUpperCase();
    if (Status == 'AWAITING PAYMENT') Status = 'AUTHORISED';
    if (Status == 'AWAITING APPROVAL') Status = 'SUBMITTED';
    const body: CF.XINVOICE_SUM = {
        funcName: 'XINVOICE_SUM',
        scottsOrgId: ScottsOrgId,
        startDate: startDateString,
        endDate: endDateString,
        status: Status || undefined,
        startDueDate: startDueDateString || undefined,
        endDueDate: endDueDateString || undefined,
        contactGroup: ContactGroup || undefined,
        contacts: flattenExcelRange(Contacts),
    };
    return enqueueCall(body);
}

/**
 * Connects to Xero and returns bill lines filtered between two dates and other filtering information.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartDate Starting date of the range to filter.
 * @param EndDate Final date of the range to filter.
 * @param [StartDueDate] Optional. Starting Due Date to filter.
 * @param [EndDueDate] Optional. Ending Due Date to filter.
 * @param [Item] Optional. Item to filter.
 * @param [Account] Optional. Xero Account to filter.
 * @param [FirstCategory] Optional. The first Tracking category to filter.
 * @param [FirstOption] Optional. The first Tracking category's option to filter.
 * @param [SecondCategory] Optional. The second Tracking category to filter.
 * @param [SecondOption] Optional. The second Tracking category's option to filter.
 * @param Contacts Contacts to filter.
 * @returns Bill lines matching the filters.
 */
export function XBILL(
    ScottsOrgId: string,
    StartDate: number,
    EndDate: number,
    StartDueDate?: number,
    EndDueDate?: number,
    Item?: string,
    Account?: string,
    FirstCategory?: string,
    FirstOption?: string,
    SecondCategory?: string,
    SecondOption?: string,
    Contacts?: string[][][]
): Promise<Array<Array<unknown>>> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const startDueDateString = (StartDueDate ?? 0) > 0 ? excelToJSDate(StartDueDate!).toISOString() : null;
    const endDueDateString = (EndDueDate ?? 0) > 0 ? excelToJSDate(EndDueDate!).toISOString() : null;
    const body: CF.XBILL = {
        funcName: 'XBILL',
        scottsOrgId: ScottsOrgId,
        startDate: startDateString,
        endDate: endDateString,
        startDueDate: startDueDateString || undefined,
        endDueDate: endDueDateString || undefined,
        item: Item || undefined,
        startAcct: Account || undefined,
        trackingCategory: FirstCategory || undefined,
        trackingOption: FirstOption || undefined,
        trackingCategory2: SecondCategory || undefined,
        trackingOption2: SecondOption || undefined,
        contacts: flattenExcelRange(Contacts),
    };
    return enqueueCall(body);
}

/**
 * Connects to Xero and returns the sum of bill lines filtered between two issue dates, due dates, planned payment dates, and contacts.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartDate Starting date of the range to filter.
 * @param EndDate Final date of the range to filter.
 * @param [Status] Optional. Status to filter.
 * @param [StartDueDate] Optional. Starting Due Date to filter.
 * @param [EndDueDate] Optional. Ending Due Date to filter.
 * @param [StartPlannedPaymentDate] Optional. Starting Planned Payment Date to filter.
 * @param [EndPlannedPaymentDate] Optional. Ending Planned Payment Date to filter.
 * @param [ContactGroup] Optional. Contact Group to filter.
 * @param Contacts Contacts to filter.
 * @returns Bill headers matching the filters.
 */
export function XBILL_HEADER(
    ScottsOrgId: string,
    StartDate: number,
    EndDate: number,
    Status?: string,
    StartDueDate?: number,
    EndDueDate?: number,
    StartPlannedPaymentDate?: number,
    EndPlannedPaymentDate?: number,
    ContactGroup?: string,
    Contacts?: string[][][]
): Promise<Array<Array<unknown>>> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const startDueDateString = (StartDueDate ?? 0) > 0 ? excelToJSDate(StartDueDate!).toISOString() : null;
    const endDueDateString = (EndDueDate ?? 0) > 0 ? excelToJSDate(EndDueDate!).toISOString() : null;
    const startPlannedPaymentDateString = (StartPlannedPaymentDate ?? 0) > 0 ? excelToJSDate(StartPlannedPaymentDate!).toISOString() : null;
    const endPlannedPaymentDateString = (EndPlannedPaymentDate ?? 0) > 0 ? excelToJSDate(EndPlannedPaymentDate!).toISOString() : null;
    Status = Status?.toLocaleUpperCase();
    if (Status == 'AWAITING PAYMENT') Status = 'AUTHORISED';
    if (Status == 'AWAITING APPROVAL') Status = 'SUBMITTED';
    const body: CF.XBILL_HEADER = {
        funcName: 'XBILL_HEADER',
        scottsOrgId: ScottsOrgId,
        startDate: startDateString,
        endDate: endDateString,
        status: Status || undefined,
        startDueDate: startDueDateString || undefined,
        endDueDate: endDueDateString || undefined,
        startPlannedPaymentDate: startPlannedPaymentDateString || undefined,
        endPlannedPaymentDate: endPlannedPaymentDateString || undefined,
        contactGroup: ContactGroup || undefined,
        contacts: flattenExcelRange(Contacts),
    };
    return enqueueCall(body);
}

/**
 * Connects to Xero and returns the Amount Due sum of bill lines filtered between two issue dates, due dates, and contacts.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartDate Starting date of the range to filter.
 * @param EndDate Final date of the range to filter.
 * @param [Status] Optional. Status to filter.
 * @param [StartDueDate] Optional. Starting Due Date to filter.
 * @param [EndDueDate] Optional. Ending Due Date to filter.
 * @param [ContactGroup] Optional. Contact Group to filter.
 * @param Contacts Contacts to filter.
 * @returns Amount Due Sum of the bills matching the filters.
 */
export function XBILL_SUM(
    ScottsOrgId: string,
    StartDate: number,
    EndDate: number,
    Status?: string,
    StartDueDate?: number,
    EndDueDate?: number,
    ContactGroup?: string,
    Contacts?: string[][][]
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const startDueDateString = (StartDueDate ?? 0) > 0 ? excelToJSDate(StartDueDate!).toISOString() : null;
    const endDueDateString = (EndDueDate ?? 0) > 0 ? excelToJSDate(EndDueDate!).toISOString() : null;
    Status = Status?.toLocaleUpperCase();
    if (Status == 'AWAITING PAYMENT') Status = 'AUTHORISED';
    if (Status == 'AWAITING APPROVAL') Status = 'SUBMITTED';
    const body: CF.XBILL_SUM = {
        funcName: 'XBILL_SUM',
        scottsOrgId: ScottsOrgId,
        startDate: startDateString,
        endDate: endDateString,
        status: Status || undefined,
        startDueDate: startDueDateString || undefined,
        endDueDate: endDueDateString || undefined,
        contactGroup: ContactGroup || undefined,
        contacts: flattenExcelRange(Contacts),
    };
    return enqueueCall(body);
}

/**
 * Connects to Xero and returns payments filtered between two dates and other filtering information.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartDate Starting date of the range to filter.
 * @param EndDate Final date of the range to filter.
 * @param [Status] Optional. The second Tracking category to filter.
 * @param [Account] Optional. Xero Account to filter.
 * @param Contacts Contacts to filter.
 * @returns Payments matching the filters.
 */
export function XPAYMENT(
    ScottsOrgId: string,
    StartDate: number,
    EndDate: number,
    Status?: string,
    Account?: string,
    Contacts?: string[][][]
): Promise<Array<Array<unknown>>> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.XPAYMENT = {
        funcName: 'XPAYMENT',
        scottsOrgId: ScottsOrgId,
        startDate: startDateString,
        endDate: endDateString,
        status: Status || undefined,
        startAcct: Account || undefined,
        contacts: flattenExcelRange(Contacts),
    };
    return enqueueCall(body);
}

/**
 * Connects to Xero and returns journal lines filtered between two dates and other filtering information.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartDate Starting date of the range to filter.
 * @param EndDate Final date of the range to filter.
 * @param [FirstCategory] Optional. The first Tracking category to filter.
 * @param [FirstOption] Optional. The first Tracking category's option to filter.
 * @param [SecondCategory] Optional. The second Tracking category to filter.
 * @param [SecondOption] Optional. The second Tracking category's option to filter.
 * @param [Accounts] GL account Names or Codes to process.
 * @returns Journal lines matching the filters.
 */
export function XGLTRX(
    ScottsOrgId: string,
    StartDate: number,
    EndDate: number,
    FirstCategory?: string,
    FirstOption?: string,
    SecondCategory?: string,
    SecondOption?: string,
    Accounts?: string[][][]
): Promise<Array<Array<unknown>>> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.XGLTRX = {
        funcName: 'XGLTRX',
        scottsOrgId: ScottsOrgId,
        startDate: startDateString,
        endDate: endDateString,
        trackingCategory: FirstCategory,
        trackingOption: FirstOption,
        trackingCategory2: SecondCategory,
        trackingOption2: SecondOption,
        accounts: flattenExcelRange(Accounts),
    };
    return enqueueCall(body);
}

/**
 * Connects to Xero and returns payments filtered between two dates and other filtering information.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartDate Starting date of the range to filter.
 * @param EndDate Final date of the range to filter.
 * @param BankAccount Xero Bank Account to filter.
 * @param [IsReconciled] Optional. Filter by reconciled or unreconciled transactions.
 * @returns Payments matching the filters.
 */
export function XBANKTRX(
    ScottsOrgId: string,
    StartDate: number,
    EndDate: number,
    BankAccount: string,
    IsReconciled?: boolean,
): Promise<Array<Array<unknown>>> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.XBANKTRX = {
        funcName: 'XBANKTRX',
        scottsOrgId: ScottsOrgId,
        startDate: startDateString,
        endDate: endDateString,
        startAcct: BankAccount,
        isReconciled: IsReconciled ?? undefined,
    };
    return enqueueCall(body);
}